<template>
  <div class="flex flex-col h-full min-h-0">
    <!-- Header -->
    <div class="flex items-center gap-2 px-2 py-2 border-b border-white/5 shrink-0">
      <button
        v-if="!isDetached(threadId)"
        type="button"
        class="p-1.5 rounded hover:bg-white/5 text-text-muted hover:text-white"
        aria-label="Back"
        @click="$emit('back')"
      >
        <el-icon size="14"><ArrowLeft /></el-icon>
      </button>
      <div v-if="thread" class="flex items-center gap-2 min-w-0 flex-1">
        <!-- Room (group / label / staff) icon bubble vs DM avatar -->
        <div v-if="isRoom" class="shrink-0 w-7 h-7 rounded-full bg-primary/15 text-primary flex items-center justify-center">
          <el-icon size="13"><UserFilled /></el-icon>
        </div>
        <div v-else class="relative shrink-0">
          <img
            :src="thread.otherUser?.avatar || '/images/default-avatar.svg'"
            :alt="thread.otherUser?.name"
            class="w-7 h-7 rounded-full object-cover"
          />
          <span
            v-if="isOnline(thread.otherUser?.id)"
            class="absolute bottom-0 right-0 w-2 h-2 rounded-full ring-1 ring-[var(--surface-color,#0d0e12)]"
            :class="otherDotClass"
          ></span>
        </div>
        <div class="min-w-0">
          <div class="flex items-center gap-1.5">
            <p class="text-sm text-white font-semibold truncate">
              {{ isRoom ? (thread.title || 'Room') : (thread.otherUser?.name || 'User') }}
            </p>
            <!-- Cross-tenant badge for superadmins -->
            <span
              v-if="thread.tenant"
              class="shrink-0 text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-primary/10 text-primary border border-primary/30"
              :title="'Cross-tenant · ' + thread.tenant.name"
            >{{ thread.tenant.name }}</span>
          </div>
          <p class="text-[10px] text-text-muted">
            <template v-if="isRoom">
              {{ thread.memberCount || 0 }} members
            </template>
            <template v-else>
              {{ otherStatusLabel }}
            </template>
          </p>
        </div>
      </div>

      <!-- Call buttons (audio / video). Only 1:1 DMs and small groups are
           call-capable (see the WS handleCallInitiate allowlist). Hidden in
           global + role-derived rooms (label / staff) where a call has no
           well-defined callee set. -->
      <ChatCallBar
        v-if="thread && callCapable"
        :thread-id="threadId"
        :thread-kind="thread.kind"
        @error="onCallError"
      />

      <!-- Detach button (desktop only; hidden when already detached) -->
      <button
        v-if="!isDetached(threadId)"
        type="button"
        class="p-1.5 rounded hover:bg-white/5 text-text-muted hover:text-white hidden md:inline-flex"
        :title="$t('chat.detach.detach')"
        :aria-label="$t('chat.detach.detach')"
        @click="detach(threadId)"
      >
        <el-icon size="14"><Rank /></el-icon>
      </button>
    </div>

    <!-- Pinned message sticky banner. Both DM participants + label LMs +
         admins can pin/unpin (server enforces; UI passes `canUnpin`
         based on the same gate). -->
    <ChatPinnedBanner
      v-if="thread?.pinnedMessageId"
      :thread-id="threadId"
      :pinned-message-id="thread.pinnedMessageId"
      :snippet="pinnedSnippet"
      :can-unpin="canPinMessages"
      @jump="onJumpTo"
      @unpinned="onUnpinned"
    />

    <ChatMessageList
      ref="messageListRef"
      class="flex-1 min-h-0"
      :messages="messages"
      :loading="loading"
      :has-more="hasMore"
      :can-pin-messages="canPinMessages"
      empty-label="No messages yet — say hi!"
      @retry="onRetry"
      @load-older="loadOlder"
      @delete="onDelete"
      @mute="onMute"
      @reply="onReplyTo"
      @pin="onPinMessage"
      @jump-to="onJumpTo"
    />

    <!-- Typing indicator — shows just above the composer when anyone in the
         thread (other than self) emits `typing.start`. -->
    <ChatTypingIndicator :thread-id="threadId" :participants="participantLookup" />

    <ChatComposer
      :sending="sending"
      :thread-id="threadId"
      :thread-kind="thread?.kind || 'dm'"
      :reply-to="replyTo"
      :author-name="currentUserPublic().name"
      :placeholder="'Message ' + (thread?.otherUser?.name || 'user') + '...'"
      @send="onSend"
      @cancel-reply="replyTo = null"
    />

    <ChatAdminMuteDialog
      v-model="muteDialogOpen"
      :user-id="muteTarget?.userId || null"
      :user-name="muteTarget?.userName || ''"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch, nextTick } from 'vue'
import { ArrowLeft, UserFilled, Rank } from '@element-plus/icons-vue'
import { ElMessage } from 'element-plus'
import { useChatThreads } from '~/composables/chat/useChatThreads'
import { usePresence } from '~/composables/chat/usePresence'
import { useChatModeration } from '~/composables/chat/useChatModeration'
import { useChatTranslation } from '~/composables/chat/useChatTranslation'
import { useDetachedWindows } from '~/composables/chat/useDetachedWindows'

const props = defineProps({
  threadId: { type: String, required: true }
})
defineEmits(['back'])

const { user } = useUserSession()
const { isOnline, getStatus } = usePresence()
const { t } = useI18n()
const { detach, isDetached } = useDetachedWindows()

const otherStatusLabel = computed(() => {
  const id = thread.value?.otherUser?.id
  if (!id) return ''
  const s = getStatus(id)
  if (s === 'offline') return t('chat.presence.offline')
  return t('chat.status.self.' + s)
})

const otherDotClass = computed(() => {
  const id = thread.value?.otherUser?.id
  if (!id) return 'bg-emerald-400'
  const s = getStatus(id)
  if (s === 'afk') return 'bg-amber-400'
  if (s === 'dnd') return 'bg-red-500'
  return 'bg-emerald-400'
})
const {
  threads, roomThreadsById, openThread, closeActiveThread,
  threadMessages, isThreadLoading, threadHasMoreFor, isThreadSending,
  loadOlderMessages, sendMessage, retrySend
} = useChatThreads()

// DM/group threads live in `threads`; role-derived rooms (label/staff) live
// in `roomThreadsById` (kept out of the DM list). Resolve from either.
const thread = computed(() =>
  threads.value.find(t => t.id === props.threadId) || roomThreadsById.get(props.threadId) || null
)
const messages = threadMessages(props.threadId)

// Room = a multi-party thread rendered with a title + member count instead of
// a single "other user" (group, label chat, staff room).
const isRoom = computed(() => {
  const k = thread.value?.kind
  return k === 'group' || k === 'label' || k === 'staff'
})
// Only 1:1 DMs and groups support calls (WS handleCallInitiate allowlist).
const callCapable = computed(() => {
  const k = thread.value?.kind
  return k === 'dm' || k === 'group'
})

// Map<userId, {name, avatar}> so the typing indicator can render names.
// For DMs only the other user is reachable via `otherUser`; groups expose
// the full `participants` list.
const participantLookup = computed(() => {
  const map = new Map()
  const t = thread.value
  if (!t) return map
  if (t.otherUser?.id) map.set(String(t.otherUser.id), { name: t.otherUser.name, avatar: t.otherUser.avatar })
  for (const p of t.participants || []) {
    if (p?.id) map.set(String(p.id), { name: p.name, avatar: p.avatar })
  }
  return map
})
const loading = isThreadLoading(props.threadId)
const hasMore = threadHasMoreFor(props.threadId)
const sending = isThreadSending(props.threadId)

function currentUserPublic() {
  const u = user.value || {}
  return {
    id: String(u.id || u._id || ''),
    name: u.chatProfile?.displayName || u.name || 'User',
    avatar: u.chatProfile?.avatarUrl || u.spotify?.imageUrl || u.soundcloud?.imageUrl || u.avatar || null,
    role: u.role || 'customer'
  }
}

function onSend(payload) {
  if (typeof payload === 'string') {
    sendMessage(props.threadId, payload, currentUserPublic())
  } else {
    sendMessage(props.threadId, payload.body, currentUserPublic(), payload.attachments || [], payload.replyTo || null)
  }
  // Clear quote target after submit — the composer doesn't know about
  // the parent's state, so we own the lifecycle here.
  replyTo.value = null
}
function onRetry(cid) { retrySend(props.threadId, cid, currentUserPublic()) }
function loadOlder() { loadOlderMessages(props.threadId) }

// Quote-reply state — set when the user clicks Reply on a message in
// the list, sent along with the next submit, cleared on send or via the
// composer's X button.
const replyTo = ref(null)
function onReplyTo(msg) {
  replyTo.value = {
    id: msg.id,
    authorName: msg.author?.name || 'User',
    bodySnippet: (msg.body || '').slice(0, 120)
  }
}

// Pin permission gate — server enforces, but the UI hides the action
// from users who can't act. DM: both participants. Group: creator OR
// admin. Label: any participant (LMs are auto-added). Global: admin only.
const meId = computed(() => String(user.value?._id || user.value?.id || ''))
const isAdmin = computed(() => user.value?.role === 'admin')
const canPinMessages = computed(() => {
  const t = thread.value
  if (!t) return false
  if (t.kind === 'global') return isAdmin.value
  if (t.kind === 'group') {
    return isAdmin.value || String(t.createdBy || '') === meId.value
  }
  // dm / label — any participant
  return true
})

// Pinned snippet — pulled from the local messages array when available;
// falls back to "Open pinned message…" placeholder if the source isn't in
// the current scrollback (older messages haven't been loaded yet).
const pinnedSnippet = computed(() => {
  const id = thread.value?.pinnedMessageId
  if (!id) return ''
  const m = (messages.value || []).find((x) => x.id === id)
  if (!m) return 'Open pinned message…'
  if (m.deletedAt) return '(message deleted)'
  return (m.body || '').slice(0, 120) || (m.attachments?.length ? '📎 Attachment' : '—')
})

async function onPinMessage(messageId) {
  try {
    await $fetch(`/api/chat/threads/${props.threadId}/pin`, {
      method: 'POST',
      body: { messageId }
    })
  } catch (err) {
    console.error('[chat:pin] failed', err)
  }
}
function onUnpinned() {
  // WS frame syncs banner state; nothing extra to do here.
}

// Jump-to — scrolls the message list to a given id (used by both the
// pinned-banner click and the quote-reply jump-to-source action). The
// list's ref exposes a `scrollToMessage(id)` method.
const messageListRef = ref(null)
function onJumpTo(messageId) {
  messageListRef.value?.scrollToMessage?.(messageId)
}

const { deleteMessage } = useChatModeration()
const muteDialogOpen = ref(false)
const muteTarget = ref(null)
function onDelete(messageId) { deleteMessage(messageId) }
function onMute(info) { muteTarget.value = info; muteDialogOpen.value = true }

// Surface call-start failures (getUserMedia denied, non-secure context,
// mediasoup unreachable) as a visible toast instead of a silent console error.
function onCallError(code) {
  const msg =
    code === 'call_requires_https' ? t('chat.call.error.requires_https')
    : code === 'NotAllowedError' ? t('chat.call.error.permission_denied')
    : code === 'NotFoundError' ? t('chat.call.error.no_device')
    : code === 'setup_failed' ? t('chat.call.error.setup_failed')
    : t('chat.call.error.generic')
  ElMessage.error(msg)
}

const { hydrateBatch } = useChatTranslation()

async function primeTranslationsFor(id) {
  const target = user.value?.chatProfile?.preferredLanguage || null
  if (!target) return
  const myId = String(user.value?.id || user.value?._id || '')
  // `messages` is a computed ref tied to this thread; wait one tick so the
  // initial history load has populated it before we batch-translate.
  await nextTick()
  const list = threadMessages(id).value
  if (Array.isArray(list) && list.length > 0) {
    hydrateBatch(list, target, myId)
  }
}

onMounted(async () => {
  await openThread(props.threadId)
  primeTranslationsFor(props.threadId)
})

watch(() => props.threadId, async (id, prev) => {
  if (id !== prev) {
    await openThread(id)
    primeTranslationsFor(id)
  }
})

onBeforeUnmount(() => { closeActiveThread() })
</script>
